import React, { useState } from 'react';

const fetchToSoData = async (tranid, authToken, offset = 0) => {
  console.log('Fetching TO/SO data for:', tranid, 'offset:', offset);

  try {
    const requestBody = { tranid, offset };

    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${apiBase}/mixo/so-to`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Failed to fetch TO/SO data');
    }

    const data = await response.json();
    console.log('TO/SO response:', data);
    
    if (data.items && data.items.length > 0) {
      // Debug: Log first few items to see the structure
      console.log('Sample items:', data.items.slice(0, 3));
      console.log('Unique price level names:', [...new Set(data.items.map(i => i.pricelevelname))]);
      
      // Group items by itemid, upcode, and linesequencenumber
      // Only use quantity from "Retail (MSRP)" price level
      // Collect all price data for different currencies/price types
      const aggregatedMap = data.items.reduce((map, item) => {
        const key = `${item.itemid ?? ''}|${item.upcode ?? ''}|${item.linesequencenumber ?? ''}`;
        const existing = map.get(key);
        
        if (existing) {
          // Merge price data from different price levels into existing item
          // Only update quantity if this is the Retail (MSRP) price level
          if (item.pricelevelname === 'Retail (MSRP)') {
            existing.quantity = Math.abs(Number(item.quantity) || 0);
          }
          
          // Collect price data from all price level entries
          // Store the usd_current_price and cad_current_price from each level
          if (item.pricelevelname === 'Retail (MSRP)') {
            existing.usd_current_price = item.usd_current_price || item.unitprice;
            existing.cad_current_price = item.cad_current_price;
          }
          
          // Collect future prices if they exist (check other price levels or specific fields)
          if (item.usd_future_price) {
            existing.usd_future_price = item.usd_future_price;
          }
          if (item.cad_future_price) {
            existing.cad_future_price = item.cad_future_price;
          }
          // Collect outlet prices
          if (item.usd_outlet_price) {
            existing.usd_outlet_price = item.usd_outlet_price;
          }
          if (item.cad_outlet_price) {
            existing.cad_outlet_price = item.cad_outlet_price;
          }
        } else {
          // First time seeing this line item
          const newItem = { ...item };
          
          // Set quantity only from Retail (MSRP) price level, use absolute value
          if (item.pricelevelname === 'Retail (MSRP)') {
            newItem.quantity = Math.abs(Number(item.quantity) || 0);
          } else {
            newItem.quantity = 0; // Will be updated when we see the MSRP entry
          }
          
          // Store current prices from the item data
          newItem.usd_current_price = item.usd_current_price || item.unitprice;
          newItem.cad_current_price = item.cad_current_price;
          newItem.usd_future_price = item.usd_future_price;
          newItem.cad_future_price = item.cad_future_price;
          newItem.usd_outlet_price = item.usd_outlet_price;
          newItem.cad_outlet_price = item.cad_outlet_price;
          
          map.set(key, newItem);
        }
        return map;
      }, new Map());

      const aggregatedItems = Array.from(aggregatedMap.values());
      console.log('Aggregated items before filter:', aggregatedItems);
      console.log('Items with quantity:', aggregatedItems.map(i => ({ 
        itemid: i.itemid, 
        quantity: i.quantity, 
        pricelevelname: i.pricelevelname,
        linesequencenumber: i.linesequencenumber
      })));
      
      const filteredItems = aggregatedItems.filter(item => item.quantity > 0);
      console.log('Items after filtering (qty > 0):', filteredItems.length);

      // Combine quantities for duplicate SKUs
      const skuMap = new Map();
      filteredItems.forEach(item => {
        const sku = item.upcode || item.itemid;
        if (skuMap.has(sku)) {
          // Add quantity to existing SKU
          const existing = skuMap.get(sku);
          existing.quantity += item.quantity;
        } else {
          // First occurrence of this SKU
          skuMap.set(sku, { ...item });
        }
      });
      
      const consolidatedItems = Array.from(skuMap.values());
      console.log('Items after SKU consolidation:', consolidatedItems.length);
      console.log(`📊 Consolidation Summary: ${data.items.length} raw records → ${aggregatedItems.length} line items → ${consolidatedItems.length} unique SKUs`);
      console.log(`📄 Pagination: hasMore=${data.hasMore}, nextOffset=${data.nextOffset}, totalResults=${data.totalResults}`);

      return {
        success: true,
        data: consolidatedItems,
        transactionType: data.transactionType || (tranid.startsWith('TO') ? 'TO' : 'SO'),
        hasMore: data.hasMore || false,
        nextOffset: data.nextOffset || 0,
        currentOffset: data.offset || 0,
        totalResults: data.totalResults || null,
        rawCount: data.items.length  // Add this for better user feedback
      };
    } else {
      return { 
        success: false, 
        error: 'No items found in this TO/SO',
        hasMore: data.hasMore || false,
        nextOffset: data.nextOffset || 0
      };
    }
  } catch (error) {
    console.error('Error fetching TO/SO data:', error);
    return { success: false, error: error.message || 'Failed to fetch TO/SO data' };
  }
};

const ToSoInput = ({ onDataFetched, authToken }) => {
  const [tranidInput, setTranidInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (event) => {
    const value = event.target.value.toUpperCase();
    setTranidInput(value);
    setError('');
  };

  const handleFetchData = async (offset = 0) => {
    if (!tranidInput) {
      setError('Please enter a TO or SO ID.');
      return;
    }
    
    // Basic validation - should start with TO or SO
    if (!tranidInput.startsWith('TO') && !tranidInput.startsWith('SO')) {
      setError('Transaction ID must start with TO or SO');
      return;
    }
    
    setIsLoading(true);
    setError('');

    const result = await fetchToSoData(tranidInput, authToken, offset);

    setIsLoading(false);
    
    if (result.success) {
      // Pass pagination metadata along with the data
      onDataFetched(result.data, tranidInput, result.transactionType, {
        hasMore: result.hasMore,
        nextOffset: result.nextOffset,
        currentOffset: result.currentOffset,
        totalResults: result.totalResults
      });
    } else {
      setError(result.error || 'Failed to fetch TO/SO data.');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      handleFetchData();
    }
  };

  return (
    <div className="toso-input-container">
      <style jsx>{`
        .toso-input-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #2c3e50;
          font-weight: 600;
        }

        .input-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .form-control {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          font-size: 16px;
          min-height: 44px;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
          box-sizing: border-box;
        }

        .form-control:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn {
          padding: 12px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 16px;
          min-height: 44px;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .btn:active {
          transform: scale(0.98);
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .btn-primary:hover:not(:disabled) {
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .text-danger {
          color: #dc3545;
          font-size: 14px;
          margin-top: 8px;
        }

        .helper-text {
          font-size: 14px;
          color: #666;
          margin-top: 4px;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .input-group {
            flex-direction: column;
            gap: 12px;
          }

          .form-control {
            width: 100%;
            padding: 16px;
            font-size: 16px;
          }

          .btn {
            width: 100%;
            padding: 16px;
            font-size: 1.1rem;
          }
        }
      `}</style>
      
      <div className="form-group">
        <label htmlFor="tranid-input"><strong>Transfer Order / Sales Order ID</strong></label>
        <div className="input-group">
          <input
            type="text"
            id="tranid-input"
            value={tranidInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter TO##### or SO######"
            className="form-control"
            disabled={isLoading}
            autoFocus
          />
          <button
            type="button"
            onClick={() => handleFetchData()}
            className="btn btn-primary"
            disabled={isLoading || !tranidInput}
          >
            {isLoading ? 'Loading...' : 'Fetch Items'}
          </button>
        </div>
    
        {error && <p className="text-danger">{error}</p>}
      </div>
    </div>
  );
};

export default ToSoInput;

